import { useMemo } from 'react'

import { Card, SkeletonBox, Chip, DescriptionList } from '@circlefin/components'
import { useDate, useMoney } from '@circlefin/hooks'
import {
  InvoicePaymentType,
  InvoiceStatus,
  PaymentMethodType,
  useInvoicePaymentDetailQuery,
} from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import type { ChipProps } from '@circlefin/components/lib/Chip'
import type { InvoiceDetail } from '@shared/graphql'

const { Label, Description } = DescriptionList

export interface InvoiceDescriptionCardProps {
  /**
   * The invoice detail that should be displayed.
   */
  invoiceDetail: InvoiceDetail
  /**
   * Additional classes that will be applied to the element.
   */
  className?: string
}

const tagVariants: Record<string, ChipProps['variant']> = {
  payment_due: 'status/error',
  paid: 'status/neutral',
  processing: 'status/neutral',
}

export const InvoiceDescription: React.FC<InvoiceDescriptionCardProps> = ({
  className,
  invoiceDetail,
  ...props
}) => {
  const { t } = useTranslation('settings')
  const { date } = useDate()
  const { money } = useMoney()
  const { data, loading } = useInvoicePaymentDetailQuery({
    variables: {
      id: invoiceDetail.id,
    },
  })
  const latestPaymentDetails = data?.invoicePaymentDetail.latestPaymentDetails

  const paymentStatus = useMemo(() => {
    if (latestPaymentDetails?.status === InvoiceStatus.pending) {
      return 'processing'
    }

    if (invoiceDetail.status === InvoiceStatus.paid) {
      return 'paid'
    }

    return 'payment_due'
  }, [latestPaymentDetails, invoiceDetail])

  const paymentMethod = useMemo(() => {
    if (!latestPaymentDetails) {
      return
    }

    if (latestPaymentDetails?.type === InvoicePaymentType.cps_card_payment) {
      return PaymentMethodType.card
    }

    return PaymentMethodType.wallet
  }, [latestPaymentDetails])

  return (
    <Card className={className} {...props}>
      <Card.Content className="px-10 py-4 font-circular">
        <div data-testid="transaction-card">
          <DescriptionList
            className="gap-y-px"
            data-testid="invoice-description-list"
            density="loose"
          >
            <Label className="w-64">
              {t('billing.invoices.details.description.issueDate')}
            </Label>
            <Description>
              <SkeletonBox className="h-6 w-56" loading={loading}>
                {date(invoiceDetail.invoiceDate, 'dateTimeWithYear')}
              </SkeletonBox>
            </Description>
            <Label className="w-64">
              {t('billing.invoices.details.description.totalReceived')}
            </Label>
            <Description>
              <SkeletonBox className="h-6 w-56" loading={loading}>
                {`${money({
                  number: invoiceDetail.amountPaid.amount,
                  variant: invoiceDetail.amountPaid.currency,
                  options: { signDisplay: false, symbol: true },
                })} / `}
                <span className="text-black-300">
                  {money({
                    number: invoiceDetail.amountTotal.amount,
                    variant: invoiceDetail.amountTotal.currency,
                    options: { signDisplay: false, symbol: true },
                  })}
                </span>
              </SkeletonBox>
            </Description>
            <Label className="w-64">
              {t('billing.invoices.details.description.status')}
            </Label>
            <Description>
              <SkeletonBox className="h-6 w-48" loading={loading}>
                {invoiceDetail?.status != null && (
                  <Chip
                    className="text-base"
                    data-testid="status-chip"
                    variant={tagVariants[paymentStatus]}
                  >
                    {t(`billing.invoices.status.${paymentStatus}`)}
                  </Chip>
                )}
              </SkeletonBox>
            </Description>
            {paymentMethod != null && (
              <>
                <Label className="w-64">
                  {t('billing.invoices.details.description.paymentMethod')}
                </Label>
                <Description>
                  <SkeletonBox className="h-6 w-56" loading={loading}>
                    {t(`billing.invoices.details.description.${paymentMethod}`)}
                  </SkeletonBox>
                </Description>
              </>
            )}
          </DescriptionList>
        </div>
      </Card.Content>
    </Card>
  )
}
